/**
 * Serialización compacta de una selección de versículos para la URL del lector
 * (`?sel=`). Hace la **selección activa compartible/recargable**: pegar la URL en
 * otro navegador reproduce exactamente los versículos seleccionados.
 *
 * Formato: lista de tokens separados por coma, donde cada token es un número
 * suelto (`9`) o un rango inclusivo `inicio-fin` (`4-6`). Ejemplo: `4-6,9`.
 * El serializador colapsa números consecutivos a rangos para mantener la URL corta.
 */

/** Parsea `?sel=` a una lista de versículos saneada, ordenada y sin duplicados. */
export function parseVerseSelection(value: unknown): number[] {
  if (typeof value !== "string" || value.trim() === "") {
    return [];
  }
  const out = new Set<number>();
  for (const rawToken of value.split(",")) {
    const token = rawToken.trim();
    if (token === "") {
      continue;
    }
    const rangeMatch = /^(\d+)-(\d+)$/.exec(token);
    if (rangeMatch) {
      let start = Number.parseInt(rangeMatch[1] ?? "", 10);
      let end = Number.parseInt(rangeMatch[2] ?? "", 10);
      if (!Number.isInteger(start) || !Number.isInteger(end)) {
        continue;
      }
      if (start > end) {
        [start, end] = [end, start];
      }
      // Cota defensiva: nunca expandir un rango absurdo desde la URL.
      const MAX_SPAN = 500;
      if (start <= 0 || end - start > MAX_SPAN) {
        continue;
      }
      for (let v = start; v <= end; v += 1) {
        out.add(v);
      }
    } else {
      const n = Number.parseInt(token, 10);
      if (Number.isInteger(n) && n > 0) {
        out.add(n);
      }
    }
  }
  return [...out].toSorted((a, b) => a - b);
}

/**
 * Serializa una lista de versículos a la forma compacta `4-6,9`. Asume entrada
 * arbitraria: ordena, deduplica y colapsa rangos consecutivos. Cadena vacía si
 * no hay selección (para omitir el param y no ensuciar la URL).
 */
export function serializeVerseSelection(verses: number[]): string {
  const sorted = [...new Set(verses.filter((v) => Number.isInteger(v) && v > 0))].toSorted(
    (a, b) => a - b,
  );
  const first = sorted[0];
  if (first === undefined) {
    return "";
  }
  const tokens: string[] = [];
  let rangeStart = first;
  let prev = rangeStart;
  for (let i = 1; i <= sorted.length; i += 1) {
    const current = sorted[i];
    if (current !== undefined && current === prev + 1) {
      prev = current;
      continue;
    }
    tokens.push(rangeStart === prev ? String(rangeStart) : `${rangeStart}-${prev}`);
    if (current !== undefined) {
      rangeStart = current;
      prev = current;
    }
  }
  return tokens.join(",");
}
